import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  askDataCopilot,
  getDataCopilotHealth,
} from "./services/dataCopilotService.js";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

const DEFAULT_SUGGESTED_QUESTIONS = Object.freeze([
  "Show leads from Johannesburg to Cape Town this month",
  "Which routes have the highest quote acceptance?",
  "Compare revenue this month vs last month",
  "Which orders look delayed or risky?",
]);

const PageShell = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeaderCard = styled(Card)`
  border-radius: 1rem;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PromptCard = styled(Card)`
  border-radius: 1rem;
`;

const PromptStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SuggestedQuestionGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
`;

const FixtureBanner = styled(Alert)`
  border-radius: 0.75rem;
`;

const MetricCard = styled(Card)`
  border-radius: 0.875rem;
`;

const FilterList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ChartGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ChartRow = styled.div`
  display: grid;
  grid-template-columns: minmax(7rem, 11rem) minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
`;

const ChartTrack = styled.div`
  height: 0.75rem;
  border-radius: 999px;
  background: #eef2ff;
  overflow: hidden;
`;

const ChartBar = styled.div`
  width: ${(props) => props.$width}%;
  height: 100%;
  border-radius: inherit;
  background: #2563eb;
`;

const EmptyStateCard = styled(Card)`
  border-radius: 1rem;
  text-align: center;
`;

const DataCopilotPage = () => {
  const [healthMode, setHealthMode] = useState("fixture");
  const [question, setQuestion] = useState(DEFAULT_SUGGESTED_QUESTIONS[0]);
  const [suggestedQuestions, setSuggestedQuestions] = useState(
    DEFAULT_SUGGESTED_QUESTIONS
  );
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadHealth() {
      const response = await getDataCopilotHealth();
      if (!mounted) return;

      if (response.success && Array.isArray(response.data?.suggestedQuestions)) {
        setSuggestedQuestions(response.data.suggestedQuestions);
      }
      if (response.success && response.data?.mode) {
        setHealthMode(response.data.mode);
      }
      setHealthLoading(false);
    }

    loadHealth();

    return () => {
      mounted = false;
    };
  }, []);

  const tableColumns = useMemo(() => {
    const columns = result?.table?.columns || [];
    return columns.map((column) => ({
      title: column.label,
      dataIndex: column.key,
      key: column.key,
      render: (value) => value || "-",
    }));
  }, [result]);

  const chartData = result?.visualization?.data || [];
  const chartValueKey = result?.visualization?.yKey;
  const maxChartValue = Math.max(
    1,
    ...chartData.map((item) => Number(item?.[chartValueKey]) || 0)
  );

  const handleAsk = async () => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) {
      setError("Enter a question before asking Data Copilot.");
      return;
    }

    setLoading(true);
    setError(null);

    const response = await askDataCopilot(trimmedQuestion);

    if (response.success) {
      setResult(response.data);
    } else {
      setError(response.error);
    }

    setLoading(false);
  };

  const handleSuggestedQuestion = (nextQuestion) => {
    setQuestion(nextQuestion);
    setError(null);
  };

  return (
    <PageShell>
      <HeaderCard>
        <HeaderContent>
          <Title level={2}>Ask Data</Title>
          <Paragraph>
            Ask operational and commercial questions in plain language. When the
            n8n webhook is configured, OS validates structured intent before
            running analytics queries.
          </Paragraph>
          <Text type="secondary">
            Mode: {healthMode === "n8n" ? "n8n intent" : "fixture"}. OS owns
            permissions and query execution; n8n interprets intent only.
          </Text>
        </HeaderContent>
      </HeaderCard>

      <PromptCard title="Ask a question">
        <PromptStack>
          <TextArea
            aria-label="Ask Data Copilot a question"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Example: Show leads from Johannesburg to Cape Town this month"
            autoSize={{ minRows: 3, maxRows: 5 }}
            disabled={loading}
          />

          <Space wrap>
            <Button type="primary" onClick={handleAsk} loading={loading}>
              Ask Data Copilot
            </Button>
            <Button onClick={() => setQuestion("")} disabled={loading}>
              Clear
            </Button>
          </Space>

          <SuggestedQuestionGrid>
            {suggestedQuestions.map((suggestedQuestion) => (
              <Button
                key={suggestedQuestion}
                size="small"
                onClick={() => handleSuggestedQuestion(suggestedQuestion)}
                disabled={loading || healthLoading}
              >
                {suggestedQuestion}
              </Button>
            ))}
          </SuggestedQuestionGrid>
        </PromptStack>
      </PromptCard>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Data Copilot could not answer"
          description={error}
        />
      )}

      {loading && (
        <EmptyStateCard>
          <Space direction="vertical" align="center">
            <Spin />
            <Text type="secondary">Asking Data Copilot...</Text>
          </Space>
        </EmptyStateCard>
      )}

      {!loading && !result && (
        <EmptyStateCard>
          <Title level={4}>Start with a suggested question</Title>
          <Paragraph>
            Results will appear here as answer summaries, metrics, filters,
            chart previews, and source rows.
          </Paragraph>
        </EmptyStateCard>
      )}

      {!loading && result && (
        <ResultsGrid>
          {result.status === "ambiguous" && (
            <Alert
              type="warning"
              showIcon
              message="Clarification needed"
              description={result.clarifyingQuestion || result.answer}
            />
          )}

          {result.status === "unsupported" && (
            <Alert
              type="warning"
              showIcon
              message="Unsupported question"
              description={result.unsupportedReason || result.answer}
            />
          )}

          {result.status === "error" && (
            <Alert
              type="error"
              showIcon
              message="Data Copilot error"
              description={result.answer}
            />
          )}

          {result.meta?.source === "fixture" && (
            <FixtureBanner
              type="info"
              showIcon
              message="Fixture mode"
              description="These are deterministic sample responses from Bubakii OS. The UI is rendering the same contract intended for the n8n-backed version."
            />
          )}

          {result.meta?.source === "n8n" &&
            !["ambiguous", "unsupported", "error"].includes(result.status) && (
              <FixtureBanner
                type="success"
                showIcon
                message="n8n mode"
                description="This answer was produced from a validated n8n intent response."
              />
            )}

          <Card title="Answer Summary">
            <Paragraph>{result.answer}</Paragraph>
            <Text type="secondary">Question: {result.question}</Text>
          </Card>

          <Row gutter={[16, 16]}>
            {(result.metrics || []).map((metric) => (
              <Col xs={24} sm={12} lg={6} key={metric.key}>
                <MetricCard>
                  <Statistic
                    title={metric.label}
                    value={formatMetricValue(metric)}
                    suffix={metric.unit === "percent" ? "%" : undefined}
                  />
                </MetricCard>
              </Col>
            ))}
          </Row>

          <Card title="Applied Filters">
            <FilterList>
              {Object.entries(result.filters || {}).map(([key, value]) => (
                <Tag key={key} color="blue">
                  {formatFilterKey(key)}: {String(value)}
                </Tag>
              ))}
            </FilterList>
          </Card>

          <Card title={result.visualization?.title || "Visualization"}>
            <ChartGrid>
              {chartData.map((item) => {
                const label = item?.[result.visualization?.xKey];
                const value = Number(item?.[chartValueKey]) || 0;
                const width = Math.round((value / maxChartValue) * 100);

                return (
                  <ChartRow key={`${label}-${value}`}>
                    <Text>{label}</Text>
                    <ChartTrack>
                      <ChartBar $width={width} />
                    </ChartTrack>
                    <Text strong>{formatChartValue(value, result.visualization?.type)}</Text>
                  </ChartRow>
                );
              })}
            </ChartGrid>
          </Card>

          <Card title="Source Rows">
            <Table
              rowKey="id"
              columns={tableColumns}
              dataSource={result.table?.rows || []}
              pagination={false}
              scroll={{ x: true }}
            />
          </Card>
        </ResultsGrid>
      )}
    </PageShell>
  );
};

function formatMetricValue(metric) {
  if (metric.unit === "zar") {
    return `R ${Number(metric.value || 0).toLocaleString("en-ZA")}`;
  }
  return metric.value;
}

function formatChartValue(value, chartType) {
  if (chartType === "line") {
    return `R ${value.toLocaleString("en-ZA")}`;
  }
  return value;
}

function formatFilterKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase());
}

export default DataCopilotPage;
